import React, { useCallback, useEffect, useState } from 'react';
import { InteractionManager } from 'react-native';
import {
  HeaderIcon,
  NaviIconHome,
  StackScreen,
  useStatusBarUpdate,
} from '@banhala/ui-kit';
import { GoodsDetailContentsSkeleton } from './components/GoodsDetailContentsSkeleton';
import { useQueryClient } from '@tanstack/react-query';
import { useHandleBackPressed, useIsForegroundNow } from 'shared/src/hooks';
import { CartIcon, SearchIcon } from 'shared/src/component';
import { getNumberRange, isNotNil, useNavigator } from 'shared/src/utils';
import { AnalyticsScreen } from 'shared/src/context';
import type { ILogging } from 'shared/src/api/types/ILogging';
import { goodsService } from 'shared/src/api/goodsService';
import { GoodsDetailContents } from './components/GoodsDetailContents';
import { useLogClickHome } from './hooks/useLogClickHome';
import { useAffiliateAccessLog } from './hooks/useAffiliateAccessLog';
import { useTrackingLinkAccessLog } from './hooks/useTrackingLinkAccessLog';
import { BottomDockedHeightProvider } from 'shared/src/hooks/useBottomDockedHeight';
import { useGoodsSno } from './hooks/useGoodsSno';
import { useFixedCouponSno } from './hooks/useFixedCouponSno';
import { queryKey } from './constant/queryKey';
import { useEntryAdInfo } from './hooks/useEntryAdInfo';
import { useGoodsFrom } from './hooks/useGoodsFrom';
import { useStopSaleGuard } from './hooks/useStopSaleGuard';
import { usePreloadQuery } from './hooks/usePreloadQuery';
import { useIsPostGoodsActionBottomSheetOpen } from './atom/isPostGoodsActionBottomSheetOpen.atom';

type GoodsDetailData = Awaited<ReturnType<typeof goodsService.getGoodsDetail>>;
type GoodsBasicData = Awaited<ReturnType<typeof goodsService.getGoodsBasic>>;

const GoodsDetailPage: React.FC = () => {
  const { navigator } = useNavigator();

  const isForeground = useIsForegroundNow();
  const [isPostGoodsActionBottomSheetOpen] =
    useIsPostGoodsActionBottomSheetOpen();

  const { onPressBack, showBackButton } = useHandleBackPressed({
    shouldSkip: isForeground && isPostGoodsActionBottomSheetOpen,
  });

  const queryClient = useQueryClient();
  const goodsSno = useGoodsSno();
  const fixedCouponSno = useFixedCouponSno();
  // 상세 진입 출처 화면(FROM). 네이티브 상세와 달리 RN 상세는 자동 부착이 없어
  // getDefaults에 넣어 모든 이벤트(cart/like 포함)에 전파한다.
  const from = useGoodsFrom();

  useStopSaleGuard();
  // 진입 즉시 critical 쿼리(basic/detail) prefetch 시작 → 네트워크 I/O 를 push
  // 전환 애니메이션과 겹쳐 진행시키고, 무거운 GoodsDetailContents 마운트 commit 과
  // 요청 burst 가 충돌하지 않게 한다. (이전: GoodsDetailContents 내부에서 호출)
  usePreloadQuery();
  useAffiliateAccessLog();
  useTrackingLinkAccessLog();
  const logClickHome = useLogClickHome();
  const entryAd = useEntryAdInfo();

  useStatusBarUpdate('white');

  // push 애니메이션이 끝난(=인터랙션 종료) 뒤에 무거운 GoodsDetailContents 를 마운트한다.
  // 그 전까지는 가벼운 스켈레톤만 그려, 첫 commit 을 가볍게 유지 → 전환이 즉시 시작.
  const [contentReady, setContentReady] = useState(false);
  useEffect(() => {
    const task = InteractionManager.runAfterInteractions(() => {
      setContentReady(true);
    });
    return () => task.cancel();
  }, []);

  const handleHomeClick = useCallback(() => {
    logClickHome();
    navigator.moveToHome();
  }, [logClickHome, navigator]);

  const getDefaults = useCallback((): ILogging | undefined => {
    const detail = queryClient.getQueryData<GoodsDetailData>(
      queryKey.goodsDetail(goodsSno, fixedCouponSno),
    );
    const basic = queryClient.getQueryData<GoodsBasicData>(
      queryKey.goodsBasic(goodsSno, fixedCouponSno),
    );
    if (!detail || !basic) return undefined;

    const categories = basic.display_categories?.[0] ?? null;

    return {
      analytics: {
        ...(from != null ? { FROM: from } : {}),
        CATEGORY_DEPTH: categories?.depth,
        CATEGORY_SNO: categories?.sno.toString(),
        CATEGORY_NAME: categories?.name,
        DELIVERY_LEAD_TIME:
          basic.delivery_type_info.delivery_type === 'standard'
            ? detail.goods.send_expectation_info?.days
            : null,
        DELIVERY_TYPE: basic.delivery_type_info.delivery_type,
        DISCOUNT_RATE: basic.price_info.discount_rate,
        GOODS_SNO: basic.sno.toString(),
        GOODS_NAME: basic.name,
        IS_NEW: basic.is_new ? '1' : '0',
        LIKES_COUNT: detail.goods.score.likes_count,
        LIKES_COUNT_RANGE: getNumberRange({
          rangeUnit: 500,
          target: detail.goods.score.likes_count,
        }),
        MARKET_SNO: basic.market.sno.toString(),
        MARKET_NAME: basic.market.name,
        MARKET_TYPE: basic.market.market_vertical_type.name,
        ORIGINAL_PRICE: basic.price_info.consumer,
        REVIEW_COUNT: detail.goods.score.total_review_count,
        REVIEW_COUNT_RANGE: getNumberRange({
          target: detail.goods.score.total_review_count,
          rangeUnit: 50,
        }),
        REVIEW_RATING: basic.positive_review_rate,
        SALES_PRICE: basic.price_info.thumbnail_price,
        SALES_PRICE_RANGE: getNumberRange({
          target: basic.price_info.thumbnail_price,
          rangeUnit: 5000,
        }),
        STANDARD_CATEGORY_SNO: basic.standard_category.sno.toString(),
        STANDARD_CATEGORY_NAME: basic.standard_category.name,
        IS_AD: isNotNil(entryAd) ? '1' : '0',
        ...detail.logging.analytics,
      },
    };
  }, [queryClient, goodsSno, fixedCouponSno, entryAd, from]);

  return (
    <AnalyticsScreen screen="GOODS_DETAIL" getDefaults={getDefaults}>
      <BottomDockedHeightProvider>
        <StackScreen
          hideBackButton={!showBackButton}
          onPressBack={onPressBack}
          backButtonTestID={'뒤로가기버튼'}
          rightIcons={[
            <HeaderIcon testID={'홈버튼'} onPress={handleHomeClick}>
              <NaviIconHome />
            </HeaderIcon>,
            <SearchIcon />,
            <CartIcon />,
          ]}
        >
          {contentReady ? (
            <GoodsDetailContents />
          ) : (
            <GoodsDetailContentsSkeleton />
          )}
        </StackScreen>
      </BottomDockedHeightProvider>
    </AnalyticsScreen>
  );
};

export default GoodsDetailPage;
